import { DAEMON_URL } from "../config";

/** Live per-request probe result from the qa-agent. "unprovisioned" means the
 *  machine is reachable but not set up to run a job yet; "unreachable" means the
 *  qa-agent could not reach it at all. Both are values to render, not errors. */
export type QaDesktopHealth = "ready" | "unprovisioned" | "unreachable";

/** One selectable QA machine. Named by `label` only: the ssh alias never leaves
 *  the qa-agent, so there is no hostname here to render by mistake. */
export interface QaDesktop {
  id: string;
  label: string;
  selected: boolean;
  health: QaDesktopHealth;
}

/** Which machine the QA desktop lane drives. The daemon proxies this to the
 *  qa-agent and injects the token server-side. `configured` is false when the
 *  daemon has no qa-agent URL/token, in which case the picker is inert. */
export interface QaDesktopsView {
  selected: string;
  desktops: QaDesktop[];
  configured: boolean;
  /** True when an env override is driving the lane, which makes `selected`
   *  decorative: the override wins until it is removed. */
  overrideActive?: boolean;
  /** Display name of the machine the override forces. May be a LABEL rather
   *  than a hostname; render it, do not parse it. */
  overrideHost?: string;
  /** Present when the qa-agent was unreachable / errored (degraded, not fatal). */
  error?: string;
}

/** May the QA desktop lane start a run on the selected machine right now, and
 *  if not, why not. The same verdict the auto-run scheduler gates on.
 *
 *  NEVER BRANCH ON `reason` TO DECIDE "IN USE" (measured, qa-agent
 *  desktop-idle-client.ts): a busy machine answers `locked_out`, never
 *  `desktop_in_use`, because the qa-agent files the lockout for the very input
 *  it is judging and the lockout clause answers first. A predicate matching
 *  `desktop_in_use` is unreachable and fails silently with a green suite.
 *  `eligible`, `inUseNow` and `lockedOutUntil` are the load-bearing fields. */
export interface QaDesktopIdleView {
  /** Registry id of the machine that was probed ("" when nothing was asked). */
  machine: string;
  /** Human-readable machine name. Never an ssh alias: that never leaves the qa-agent. */
  label: string;
  /** null when the probe failed. Callers must NOT read null as "0ms idle". */
  msSinceHumanInput: number | null;
  /** Epoch ms until which the machine is locked out. 0 = no lockout on file. */
  lockedOutUntil: number;
  /** May the lane drive this machine right now? */
  eligible: boolean;
  /** Is a human at the machine at this instant? True before any lockout is filed. */
  inUseNow: boolean;
  /** Machine-readable cause, from either the qa-agent (`probe_failed`,
   *  `locked_out`, `desktop_in_use`) or the daemon (`not_configured`,
   *  `unreachable`, `upstream_<status>`). Classify with it; do not render it. */
  reason?: string;
  /** False when the daemon has no qa-agent URL/token: nothing was even asked. */
  configured: boolean;
}

/** The result of a switch. `persisted: false` means the choice took effect but
 *  did not reach disk, so it reverts when the qa-agent restarts. */
export interface QaDesktopSwitch {
  selected: string;
  label: string;
  persisted: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
  /** Set when `persisted` is false: the exact sentence to show the operator. */
  warning?: string;
}

async function errorFrom(res: Response): Promise<Error> {
  let msg = `http ${res.status}`;
  try {
    const j = (await res.json()) as { error?: string; reason?: string };
    // `reason` is the qa-agent's human-readable refusal (e.g. a run is still
    // driving a machine); it is the only part an operator can act on, so it
    // wins over the machine-readable code.
    if (j?.reason) msg = j.reason;
    else if (j?.error) msg = j.error;
  } catch { /* keep status */ }
  return new Error(msg);
}

/** Read the machine list and which one is selected. The daemon injects the
 *  qa-agent token and degrades softly (200 + an empty list + `error`) on any
 *  upstream/config problem, so this only throws on a genuine dashboard-auth
 *  failure. */
export async function getQaDesktops(): Promise<QaDesktopsView> {
  const res = await fetch(`${DAEMON_URL}/qa/desktops`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as QaDesktopsView;
}

/** Read whether the QA lane may drive the selected machine right now.
 *
 *  Shaped exactly like getQaDesktops: the daemon injects the qa-agent token and
 *  degrades every upstream problem into a VALUE (`eligible: false` plus a
 *  `reason`), so this only throws on a genuine dashboard-auth failure.
 *
 *  NOT free upstream. It probes the machine over ssh behind an 8s budget, and a
 *  machine somebody is using files a lockout as a side effect of being asked.
 *  Read it on mount and when the operator asks; never on a tight timer. */
export async function getQaDesktopIdle(): Promise<QaDesktopIdleView> {
  const res = await fetch(`${DAEMON_URL}/qa/desktop-idle`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as QaDesktopIdleView;
}

/** Point the QA desktop lane at another machine.
 *
 *  Both 200 and 202 mean the switch TOOK EFFECT, and `res.ok` covers both: 202
 *  says only that the choice did not reach disk, which the returned
 *  `persisted: false` (plus `warning`) reports. Treating 202 as a failure would
 *  show an error for a switch that worked and leave the UI on the old machine.
 *
 *  Throws on a refusal (400/409) or an upstream failure, with the qa-agent's own
 *  `reason` as the message so the caller can surface it inline. */
export async function setQaDesktop(id: string): Promise<QaDesktopSwitch> {
  const res = await fetch(`${DAEMON_URL}/qa/desktop`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as QaDesktopSwitch;
}
